
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import hostlink from '../Hostlink/hostlink';
import * as XLSX from 'xlsx';
import {
    PDFDownloadLink,
    Document,
    Page,
    Text,
    View,
    StyleSheet
} from '@react-pdf/renderer';

const Rpt_SaleReport_Return = () => {
    const usershopid = localStorage.getItem('MYPCKARTUSER')
        ? JSON.parse(localStorage.getItem('MYPCKARTUSER'))[0].id
        : '0';

    const today = new Date();

    const [data, setData] = useState([]);
    const [mobileNo, setmobileNo] = useState([]);
    const [filteredDataMob, setFilteredDataMob] = useState([]);
    const [loading, setLoading] = useState(false);

    // Customer / Mobile search - LOCAL ONLY
    const [searchTerm, setSearchTerm] = useState('');

    // Lot search - LOCAL ONLY
    const [lotSearch, setLotSearch] = useState('');

    const [frmdata, setfrmdata] = useState({
        fromdate: today.toISOString().split('T')[0],
        todate: today.toISOString().split('T')[0],
        deptcode: 0,
        status: 0
    });

    const [itemdetail, setitemdetail] = useState({
        mobno: '',
        custname: '',
        custadd: '',
        custstate: '',
        avg: '',
        style: '',
        size: '',
        pcs: '',
        rawcode: 0,
        washqty: 0,
        Washername: '',
        Washercode: 0,
        rawname: '',
        barcode: '',
        sortno: '',
        billno: '',
        billdate: today.toISOString().split('T')[0],
        mtr: '',
        roll: '',
        qty: 1,
        rate: 0,
        discperc: 0,
        taxable: 0,
        gst: 0,
        gstamt: 0,
        ittotal: 0,
        totqty: 0,
        totgst: 0,
        totordamt: 0,
        rcode: '',
        roomnoview: ''
    });

    const { fromdate, todate } = frmdata;

    const tableRef = useRef(null);

    const formatNumber = (value) => {
        return Number(value || 0).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const onChangevalue = (e) => {
        const { name, value } = e.target;

        setfrmdata((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    // =========================================================
    // CUSTOMER SEARCH - LOCAL ONLY
    // =========================================================
    const handleSearch = (e) => {
        const value = e.target.value;
        const query = value.toLowerCase().trim();

        setSearchTerm(value);

        setitemdetail((prev) => ({
            ...prev,
            mobno: value
        }));

        if (!query) {
            setFilteredDataMob([]);
            return;
        }

        const matches = mobileNo
            .filter((item) => {
                const mobno = String(item?.mobno || '').toLowerCase();
                const custname = String(item?.custname || '').toLowerCase();
                const custadd = String(item?.custadd || '').toLowerCase();
                const custstate = String(item?.custstate || '').toLowerCase();

                return (
                    mobno.includes(query) ||
                    custname.includes(query) ||
                    custadd.includes(query) ||
                    custstate.includes(query)
                );
            })
            .slice(0, 20);

        setFilteredDataMob(matches);
    };

    // =========================================================
    // SELECT CUSTOMER - NO API CALL
    // =========================================================
    const handleSelect = (item) => {
        setSearchTerm(item?.custname || item?.mobno || '');

        setitemdetail((prev) => ({
            ...prev,
            mobno: item?.mobno || '',
            custname: item?.custname || '',
            custadd: item?.custadd || '',
            custstate: item?.custstate || ''
        }));

        setFilteredDataMob([]);
    };

    // =========================================================
    // LOAD SALE RETURN DATA
    // Only API used for report data
    // =========================================================
    const loaddata = async () => {
        try {
            setLoading(true);

            const response = await axios.get(
                `${hostlink}/PurchaseMasviewrpt/6/${usershopid}/${fromdate}/${todate}`
            );

            setData(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Sale Return Report Error:', error);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    // =========================================================
    // LOAD CUSTOMER MASTER ONCE
    // =========================================================
    useEffect(() => {
        const loadCustomerMaster = async () => {
            try {
                const response = await axios.get(
                    `${hostlink}/MobileNoSearch/${usershopid}`
                );

                setmobileNo(
                    Array.isArray(response.data)
                        ? response.data
                        : []
                );
            } catch (error) {
                console.error('Customer Master Error:', error);
                setmobileNo([]);
            }
        };

        loadCustomerMaster();
        loaddata();
    }, []);

    // =========================================================
    // LOCAL FILTER
    // Customer + Mobile + Lot No
    // =========================================================
    const filteredData = useMemo(() => {
        const lotQuery = lotSearch.toLowerCase().trim();
        const customerQuery = searchTerm.toLowerCase().trim();

        if (!lotQuery && !customerQuery) {
            return data;
        }

        return data.filter((res) => {
            const lotNo = String(
                res?.billno ??
                res?.purchaseMas?.billno ??
                ''
            ).toLowerCase();

            const mobile = String(res?.mobno || '').toLowerCase();
            const customer = String(res?.custname || '').toLowerCase();
            const address = String(res?.custadd || '').toLowerCase();
            const state = String(res?.custstate || '').toLowerCase();

            const lotMatch =
                !lotQuery ||
                lotNo.includes(lotQuery);

            const customerMatch =
                !customerQuery ||
                mobile.includes(customerQuery) ||
                customer.includes(customerQuery) ||
                address.includes(customerQuery) ||
                state.includes(customerQuery);

            return lotMatch && customerMatch;
        });
    }, [data, lotSearch, searchTerm]);

    // =========================================================
    // SUMMARY
    // =========================================================
    const summary = useMemo(() => {
        const totalPcs = filteredData.reduce(
            (total, res) =>
                total + Number(res?.salestock || 0) * -1,
            0
        );

        const uniqueLots = new Set(
            filteredData
                .map((res) => res?.billno)
                .filter(Boolean)
        ).size;

        const uniqueCustomers = new Set(
            filteredData
                .map((res) => res?.custname)
                .filter(Boolean)
        ).size;

        return {
            records: filteredData.length,
            lots: uniqueLots,
            customers: uniqueCustomers,
            pcs: totalPcs
        };
    }, [filteredData]);

    // =========================================================
    // CLEAR CUSTOMER
    // =========================================================
    const clearCustomerSearch = () => {
        setSearchTerm('');
        setFilteredDataMob([]);

        setitemdetail((prev) => ({
            ...prev,
            mobno: '',
            custname: '',
            custadd: '',
            custstate: ''
        }));
    };

    // =========================================================
    // CLEAR ALL
    // =========================================================
    const clearAllFilters = () => {
        setLotSearch('');
        clearCustomerSearch();
    };

    // =========================================================
    // EXCEL
    // =========================================================
    const exportToExcel = () => {
        if (
            !tableRef.current ||
            filteredData.length === 0
        ) {
            return;
        }

        const ws = XLSX.utils.table_to_sheet(
            tableRef.current
        );

        const wb = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            wb,
            ws,
            'Sale Return Report'
        );

        XLSX.writeFile(
            wb,
            'Sale Return Report.xlsx'
        );
    };

    const pdfFileName =
        lotSearch.trim() || searchTerm.trim()
            ? `Sale Return Report - ${lotSearch || searchTerm}.pdf`
            : 'Sale Return Report.pdf';

    return (
        <div
            className="container-fluid py-3"
            style={{
                background: '#f6f8fb',
                minHeight: '100vh'
            }}
        >

            {/* =====================================================
                HEADER + FILTER
            ====================================================== */}

            <div
                className="card border-0 mb-3"
                style={{
                    borderRadius: '18px',
                    overflow: 'visible',
                    boxShadow:
                        '0 6px 24px rgba(15,23,42,0.07)'
                }}
            >

                {/* HEADER */}

                <div
                    style={{
                        background:
                            'linear-gradient(135deg,#dc3545,#ff6b81)',
                        padding: '18px 20px',
                        color: '#fff',
                        borderRadius: '18px 18px 0 0'
                    }}
                >

                    <div
                        className="d-flex justify-content-between align-items-center flex-wrap gap-2"
                    >

                        <div className="d-flex align-items-center">

                            <div
                                className="d-flex align-items-center justify-content-center me-3"
                                style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '14px',
                                    background:
                                        'rgba(255,255,255,0.18)'
                                }}
                            >
                                <i
                                    className="fa-solid fa-rotate-left"
                                    style={{
                                        fontSize: '21px'
                                    }}
                                ></i>
                            </div>

                            <div>

                                <div
                                    style={{
                                        fontSize: '20px',
                                        fontWeight: '700'
                                    }}
                                >
                                    Sale Return Report
                                </div>

                                <div
                                    style={{
                                        fontSize: '12px',
                                        opacity: 0.88
                                    }}
                                >
                                    Customer-wise sale return details
                                </div>

                            </div>

                        </div>

                        <div
                            className="badge rounded-pill"
                            style={{
                                background:
                                    'rgba(255,255,255,0.16)',
                                padding: '9px 13px',
                                fontSize: '11px'
                            }}
                        >
                            {filteredData.length} Records
                        </div>

                    </div>

                </div>


                {/* FILTER AREA */}

                <div className="card-body p-3">

                    <div className="row g-2 align-items-end">

                        {/* LOT SEARCH */}

                        <div className="col-12 col-md-6 col-lg-3">

                            <label
                                className="form-label mb-1"
                                style={{
                                    fontSize: '12px',
                                    fontWeight: '600'
                                }}
                            >
                                Lot No
                            </label>

                            <div className="input-group input-group-sm">

                                <span
                                    className="input-group-text"
                                    style={{
                                        background: '#fff',
                                        borderRadius:
                                            '10px 0 0 10px'
                                    }}
                                >
                                    <i className="fa-solid fa-magnifying-glass text-danger"></i>
                                </span>

                                <input
                                    type="text"
                                    value={lotSearch}
                                    onChange={(e) =>
                                        setLotSearch(
                                            e.target.value
                                        )
                                    }
                                    placeholder="Search Lot No..."
                                    className="form-control"
                                    style={{
                                        borderRadius:
                                            lotSearch
                                                ? '0'
                                                : '0 10px 10px 0'
                                    }}
                                />

                                {lotSearch && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() =>
                                            setLotSearch('')
                                        }
                                        style={{
                                            borderRadius:
                                                '0 10px 10px 0'
                                        }}
                                    >
                                        <i className="fa-solid fa-xmark"></i>
                                    </button>
                                )}

                            </div>

                        </div>


                        {/* CUSTOMER SEARCH */}

                        <div
                            className="col-12 col-md-6 col-lg-3"
                            style={{
                                position: 'relative'
                            }}
                        >

                            <label
                                className="form-label mb-1"
                                style={{
                                    fontSize: '12px',
                                    fontWeight: '600'
                                }}
                            >
                                Customer / Mobile
                            </label>

                            <div className="input-group input-group-sm">

                                <span
                                    className="input-group-text"
                                    style={{
                                        background: '#fff',
                                        borderRadius:
                                            '10px 0 0 10px'
                                    }}
                                >
                                    <i className="fa-solid fa-user text-danger"></i>
                                </span>

                                <input
                                    type="text"
                                    placeholder="Search Name / Mobile..."
                                    className="form-control"
                                    value={searchTerm}
                                    onChange={handleSearch}
                                    autoComplete="off"
                                    style={{
                                        borderRadius:
                                            searchTerm
                                                ? '0'
                                                : '0 10px 10px 0'
                                    }}
                                />

                                {searchTerm && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={
                                            clearCustomerSearch
                                        }
                                        style={{
                                            borderRadius:
                                                '0 10px 10px 0'
                                        }}
                                    >
                                        <i className="fa-solid fa-xmark"></i>
                                    </button>
                                )}

                            </div>


                            {/* CUSTOMER SUGGESTIONS */}

                            {filteredDataMob.length > 0 && (

                                <div
                                    style={{
                                        position: 'absolute',
                                        left: '12px',
                                        right: '12px',
                                        top: '58px',
                                        zIndex: 50,
                                        background: '#fff',
                                        border:
                                            '1px solid #e5e7eb',
                                        borderRadius: '12px',
                                        boxShadow:
                                            '0 12px 30px rgba(15,23,42,0.15)',
                                        overflow: 'hidden'
                                    }}
                                >

                                    <div
                                        style={{
                                            padding:
                                                '8px 12px',
                                            background:
                                                '#f8fafc',
                                            borderBottom:
                                                '1px solid #eef2f7',
                                            fontSize: '10px',
                                            fontWeight: '700',
                                            color: '#64748b',
                                            textTransform:
                                                'uppercase'
                                        }}
                                    >
                                        Customers Found
                                    </div>

                                    <div
                                        style={{
                                            maxHeight: '220px',
                                            overflowY: 'auto'
                                        }}
                                    >

                                        {filteredDataMob.map(
                                            (item, index) => (

                                                <div
                                                    key={`${item?.mobno || 'customer'}-${index}`}
                                                    onClick={() =>
                                                        handleSelect(
                                                            item
                                                        )
                                                    }
                                                    style={{
                                                        cursor:
                                                            'pointer',
                                                        padding:
                                                            '10px 12px',
                                                        borderBottom:
                                                            '1px solid #f1f5f9'
                                                    }}
                                                    onMouseEnter={(
                                                        e
                                                    ) => {
                                                        e.currentTarget.style.background =
                                                            '#fff5f5';
                                                    }}
                                                    onMouseLeave={(
                                                        e
                                                    ) => {
                                                        e.currentTarget.style.background =
                                                            '#fff';
                                                    }}
                                                >

                                                    <div
                                                        className="fw-semibold text-dark"
                                                        style={{
                                                            fontSize:
                                                                '12px'
                                                        }}
                                                    >
                                                        {item?.custname ||
                                                            '-'}
                                                    </div>

                                                    <div
                                                        className="text-muted"
                                                        style={{
                                                            fontSize:
                                                                '11px'
                                                        }}
                                                    >
                                                        {item?.mobno ||
                                                            '-'}
                                                        {' • '}
                                                        {item?.custstate ||
                                                            '-'}
                                                    </div>

                                                </div>

                                            )
                                        )}

                                    </div>

                                </div>

                            )}

                        </div>


                        {/* FROM DATE */}

                        <div className="col-6 col-md-3 col-lg-2">

                            <label
                                className="form-label mb-1"
                                style={{
                                    fontSize: '12px',
                                    fontWeight: '600'
                                }}
                            >
                                From Date
                            </label>

                            <input
                                type="date"
                                name="fromdate"
                                value={fromdate}
                                max={todate}
                                onChange={onChangevalue}
                                className="form-control form-control-sm"
                                style={{
                                    borderRadius: '10px'
                                }}
                            />

                        </div>


                        {/* TO DATE */}

                        <div className="col-6 col-md-3 col-lg-2">

                            <label
                                className="form-label mb-1"
                                style={{
                                    fontSize: '12px',
                                    fontWeight: '600'
                                }}
                            >
                                To Date
                            </label>

                            <input
                                type="date"
                                name="todate"
                                value={todate}
                                min={fromdate}
                                onChange={onChangevalue}
                                className="form-control form-control-sm"
                                style={{
                                    borderRadius: '10px'
                                }}
                            />

                        </div>


                        {/* BUTTONS */}

                        <div className="col-12 col-lg-4">

                            <div className="d-flex gap-2 flex-wrap">

                                <button
                                    onClick={loaddata}
                                    className="btn btn-danger btn-sm px-3"
                                    disabled={loading}
                                    style={{
                                        borderRadius: '10px',
                                        fontWeight: '600'
                                    }}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1"></span>
                                            Loading
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-magnifying-glass-chart me-1"></i>
                                            Show
                                        </>
                                    )}
                                </button>


                                <button
                                    onClick={clearAllFilters}
                                    className="btn btn-outline-secondary btn-sm px-3"
                                    style={{
                                        borderRadius: '10px',
                                        fontWeight: '600'
                                    }}
                                >
                                    <i className="fa-solid fa-filter-circle-xmark me-1"></i>
                                    Clear
                                </button>


                                <button
                                    onClick={exportToExcel}
                                    className="btn btn-outline-success btn-sm px-3"
                                    disabled={
                                        filteredData.length === 0
                                    }
                                    style={{
                                        borderRadius: '10px',
                                        fontWeight: '600'
                                    }}
                                >
                                    <i className="fa-solid fa-file-excel me-1"></i>
                                    Excel
                                </button>


                                <PDFDownloadLink
                                    document={
                                        <PDFDocument
                                            data={filteredData}
                                            fromdate={fromdate}
                                            todate={todate}
                                        />
                                    }
                                    fileName={pdfFileName}
                                    style={{
                                        textDecoration: 'none'
                                    }}
                                >
                                    {({
                                        loading: pdfLoading
                                    }) => (
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger btn-sm px-3"
                                            disabled={
                                                pdfLoading ||
                                                filteredData.length === 0
                                            }
                                            style={{
                                                borderRadius: '10px',
                                                fontWeight: '600'
                                            }}
                                        >
                                            <i className="fa-solid fa-file-pdf me-1"></i>
                                            {pdfLoading
                                                ? 'Loading'
                                                : 'PDF'}
                                        </button>
                                    )}
                                </PDFDownloadLink>

                            </div>

                        </div>

                    </div>

                </div>

            </div>


            {/* =====================================================
                KPI CARDS
            ====================================================== */}

            <div className="row g-2 mb-3">

                <div className="col-6 col-md-3">

                    <SummaryCard
                        title="Lots"
                        value={summary.lots}
                        icon="fa-boxes-stacked"
                        subtitle="Unique return lots"
                    />

                </div>


                <div className="col-6 col-md-3">

                    <SummaryCard
                        title="Records"
                        value={summary.records}
                        icon="fa-list"
                        subtitle="Return records"
                    />

                </div>


                <div className="col-6 col-md-3">

                    <SummaryCard
                        title="Customers"
                        value={summary.customers}
                        icon="fa-users"
                        subtitle="Unique customers"
                    />

                </div>


                <div className="col-6 col-md-3">

                    <SummaryCard
                        title="Returned Pcs"
                        value={formatNumber(summary.pcs)}
                        icon="fa-rotate-left"
                        subtitle="Total returned pieces"
                    />

                </div>

            </div>


            {/* =====================================================
                ACTIVE FILTER
            ====================================================== */}

            {(lotSearch || searchTerm) && (

                <div
                    className="bg-white mb-3 px-3 py-2"
                    style={{
                        borderRadius: '12px',
                        border: '1px solid #e9edf3',
                        boxShadow:
                            '0 3px 12px rgba(0,0,0,0.04)'
                    }}
                >

                    <div
                        className="d-flex align-items-center flex-wrap gap-2"
                    >

                        <span
                            className="text-muted"
                            style={{
                                fontSize: '11px',
                                fontWeight: '600'
                            }}
                        >
                            Active Filters:
                        </span>


                        {lotSearch && (

                            <span className="badge bg-danger rounded-pill">
                                Lot: {lotSearch}
                            </span>

                        )}


                        {searchTerm && (

                            <span className="badge bg-secondary rounded-pill">
                                Customer: {searchTerm}
                            </span>

                        )}


                        <span
                            className="text-muted"
                            style={{
                                fontSize: '11px'
                            }}
                        >
                            {filteredData.length} result(s)
                        </span>

                    </div>

                </div>

            )}


            {/* =====================================================
                TABLE
            ====================================================== */}

            <div
                className="card border-0"
                style={{
                    borderRadius: '18px',
                    overflow: 'hidden',
                    boxShadow:
                        '0 6px 24px rgba(15,23,42,0.07)'
                }}
            >

                <div
                    className="card-header bg-white border-0 d-flex justify-content-between align-items-center flex-wrap gap-2"
                    style={{
                        padding: '15px 18px'
                    }}
                >

                    <div>

                        <div
                            className="fw-bold"
                            style={{
                                color: '#1f2937'
                            }}
                        >
                            Sale Return Details
                        </div>

                        <small className="text-muted">
                            Lot-wise customer return details
                        </small>

                    </div>


                    <div
                        className="badge rounded-pill"
                        style={{
                            background: '#fff1f2',
                            color: '#dc3545',
                            padding: '8px 12px'
                        }}
                    >
                        {filteredData.length} rows
                    </div>

                </div>


                <div
                    style={{
                        overflowX: 'auto',
                        maxHeight: '65vh'
                    }}
                >

                    <table
                        ref={tableRef}
                        className="table table-hover align-middle mb-0"
                        style={{
                            minWidth: '1100px',
                            fontSize: '13px'
                        }}
                    >

                        <thead
                            style={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 5,
                                background: '#f8fafc'
                            }}
                        >

                            <tr>

                                <th className="text-start">
                                    Lot No
                                </th>

                                <th className="text-start">
                                    Date
                                </th>

                                <th className="text-start">
                                    Cust Mob
                                </th>

                                <th className="text-start">
                                    Cust Name
                                </th>

                                <th className="text-start">
                                    Cust Add
                                </th>

                                <th className="text-start">
                                    Cust State
                                </th>

                                <th className="text-start">
                                    Style
                                </th>

                                <th className="text-start">
                                    Size
                                </th>

                                <th className="text-end">
                                    Pcs
                                </th>

                            </tr>

                        </thead>


                        <tbody>

                            {loading ? (

                                <tr>

                                    <td
                                        colSpan="9"
                                        className="text-center"
                                        style={{
                                            padding: '60px'
                                        }}
                                    >

                                        <div className="spinner-border text-danger"></div>

                                        <div
                                            className="text-muted mt-2"
                                            style={{
                                                fontSize: '12px'
                                            }}
                                        >
                                            Loading sale return report...
                                        </div>

                                    </td>

                                </tr>

                            ) : filteredData.length === 0 ? (

                                <tr>

                                    <td
                                        colSpan="9"
                                        className="text-center"
                                        style={{
                                            padding: '60px 20px'
                                        }}
                                    >

                                        <div
                                            className="d-flex align-items-center justify-content-center mx-auto mb-3"
                                            style={{
                                                width: '58px',
                                                height: '58px',
                                                borderRadius: '18px',
                                                background: '#fff1f2',
                                                color: '#fb7185'
                                            }}
                                        >
                                            <i
                                                className="fa-solid fa-box-open"
                                                style={{
                                                    fontSize: '24px'
                                                }}
                                            ></i>
                                        </div>


                                        <div className="fw-bold text-dark">
                                            No sale return records found
                                        </div>


                                        <div
                                            className="text-muted mt-1"
                                            style={{
                                                fontSize: '12px'
                                            }}
                                        >
                                            Try another Lot No, customer or date range.
                                        </div>

                                    </td>

                                </tr>

                            ) : (

                                filteredData.map(
                                    (res, index) => {

                                        const previous =
                                            index > 0
                                                ? filteredData[
                                                    index - 1
                                                ]
                                                : null;

                                        const isDuplicate =
                                            previous &&
                                            previous.billno ===
                                                res.billno;

                                        return (

                                            <tr key={index}>

                                                <td
                                                    className="text-start fw-semibold"
                                                    style={{
                                                        color:
                                                            '#dc3545',
                                                        padding:
                                                            '11px 14px'
                                                    }}
                                                >
                                                    {isDuplicate
                                                        ? ''
                                                        : res.billno}
                                                </td>


                                                <td
                                                    className="text-start"
                                                    style={{
                                                        padding:
                                                            '11px 14px'
                                                    }}
                                                >
                                                    {isDuplicate
                                                        ? ''
                                                        : res.billdate
                                                            ? new Date(
                                                                res.billdate
                                                            ).toLocaleDateString(
                                                                'en-GB',
                                                                {
                                                                    day: '2-digit',
                                                                    month: '2-digit',
                                                                    year: '2-digit'
                                                                }
                                                            )
                                                            : '-'}
                                                </td>


                                                <td
                                                    className="text-start"
                                                    style={{
                                                        padding:
                                                            '11px 14px'
                                                    }}
                                                >
                                                    {isDuplicate
                                                        ? ''
                                                        : res.mobno}
                                                </td>


                                                <td
                                                    className="text-start fw-semibold"
                                                    style={{
                                                        padding:
                                                            '11px 14px'
                                                    }}
                                                >
                                                    {res.custname ||
                                                        '-'}
                                                </td>


                                                <td
                                                    className="text-start"
                                                    style={{
                                                        padding:
                                                            '11px 14px'
                                                    }}
                                                >
                                                    {res.custadd ||
                                                        '-'}
                                                </td>


                                                <td
                                                    className="text-start"
                                                    style={{
                                                        padding:
                                                            '11px 14px'
                                                    }}
                                                >
                                                    {res.custstate ||
                                                        '-'}
                                                </td>


                                                <td
                                                    className="text-start"
                                                    style={{
                                                        padding:
                                                            '11px 14px'
                                                    }}
                                                >
                                                    {res.style || '-'}
                                                </td>


                                                <td
                                                    className="text-start"
                                                    style={{
                                                        padding:
                                                            '11px 14px'
                                                    }}
                                                >
                                                    {res.size || '-'}
                                                </td>


                                                <td
                                                    className="text-end fw-semibold"
                                                    style={{
                                                        padding:
                                                            '11px 14px'
                                                    }}
                                                >
                                                    {formatNumber(
                                                        Number(
                                                            res.salestock ||
                                                                0
                                                        ) * -1
                                                    )}
                                                </td>

                                            </tr>

                                        );

                                    }
                                )

                            )}

                        </tbody>


                        {!loading &&
                            filteredData.length > 0 && (

                                <tfoot>

                                    <tr
                                        style={{
                                            position:
                                                'sticky',
                                            bottom: 0,
                                            zIndex: 4,
                                            background:
                                                '#fff1f2',
                                            borderTop:
                                                '2px solid #fecdd3'
                                        }}
                                    >

                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>

                                        <td
                                            className="text-end fw-bold"
                                            style={{
                                                padding:
                                                    '13px 14px'
                                            }}
                                        >
                                            Total :
                                        </td>

                                        <td
                                            className="text-end fw-bold"
                                            style={{
                                                padding:
                                                    '13px 14px',
                                                color: '#dc3545'
                                            }}
                                        >
                                            {formatNumber(
                                                summary.pcs
                                            )}
                                        </td>

                                    </tr>

                                </tfoot>

                            )}

                    </table>

                </div>

            </div>

        </div>
    );
};


// =============================================================
// SUMMARY CARD
// =============================================================

const SummaryCard = ({
    title,
    value,
    icon,
    subtitle
}) => {

    return (

        <div
            className="bg-white h-100"
            style={{
                borderRadius: '16px',
                padding: '16px',
                boxShadow:
                    '0 4px 18px rgba(0,0,0,0.06)',
                border:
                    '1px solid #edf0f5'
            }}
        >

            <div
                className="d-flex justify-content-between align-items-center"
            >

                <div>

                    <div
                        className="text-muted"
                        style={{
                            fontSize: '11px',
                            fontWeight: '600',
                            textTransform:
                                'uppercase',
                            letterSpacing:
                                '0.5px'
                        }}
                    >
                        {title}
                    </div>


                    <div
                        className="fw-bold mt-1"
                        style={{
                            fontSize: '20px',
                            color: '#1f2937'
                        }}
                    >
                        {value}
                    </div>


                    <div
                        className="text-muted mt-1"
                        style={{
                            fontSize: '10px'
                        }}
                    >
                        {subtitle}
                    </div>

                </div>


                <div
                    className="d-flex align-items-center justify-content-center"
                    style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '12px',
                        background: '#fff1f2',
                        color: '#dc3545'
                    }}
                >
                    <i
                        className={`fa-solid ${icon}`}
                    ></i>
                </div>

            </div>

        </div>

    );
};


// =============================================================
// PDF DOCUMENT
// =============================================================

const PDFDocument = ({
    data,
    fromdate,
    todate
}) => {

    const totalPcs = data.reduce(
        (total, res) =>
            total +
            Number(res?.salestock || 0) * -1,
        0
    );

    const formatNumber = (value) =>
        Number(value || 0).toFixed(2);

    return (

        <Document>

            <Page
                orientation="landscape"
                size="A4"
                style={pdfStyles.container}
            >

                <Text style={pdfStyles.title}>
                    Sale Return Report
                </Text>


                <Text style={pdfStyles.subTitle}>
                    From : {fromdate} To : {todate}
                </Text>


                <View style={pdfStyles.table}>

                    {/* HEADER */}

                    <View
                        style={pdfStyles.tableRow}
                        fixed
                    >

                        <Text style={pdfStyles.headerCell}>
                            Lot No
                        </Text>

                        <Text style={pdfStyles.headerCell}>
                            Date
                        </Text>

                        <Text style={pdfStyles.headerCell}>
                            Cust Mob
                        </Text>

                        <Text style={pdfStyles.headerCell}>
                            Cust Name
                        </Text>

                        <Text style={pdfStyles.headerCell}>
                            Cust Add
                        </Text>

                        <Text style={pdfStyles.headerCell}>
                            Cust State
                        </Text>

                        <Text style={pdfStyles.headerCell}>
                            Style
                        </Text>

                        <Text style={pdfStyles.headerCell}>
                            Size
                        </Text>

                        <Text style={pdfStyles.headerCell}>
                            Pcs
                        </Text>

                    </View>


                    {/* DATA */}

                    {data.map((res, index) => {

                        const previous =
                            index > 0
                                ? data[index - 1]
                                : null;

                        const isDuplicate =
                            previous &&
                            previous.billno ===
                                res.billno;

                        const date =
                            res.billdate
                                ? new Date(
                                    res.billdate
                                ).toLocaleDateString(
                                    'en-GB',
                                    {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: '2-digit'
                                    }
                                )
                                : '-';

                        return (

                            <View
                                key={index}
                                style={
                                    pdfStyles.tableRow
                                }
                            >

                                <Text
                                    style={
                                        pdfStyles.cell
                                    }
                                >
                                    {isDuplicate
                                        ? ''
                                        : res.billno || ''}
                                </Text>


                                <Text
                                    style={
                                        pdfStyles.cell
                                    }
                                >
                                    {isDuplicate
                                        ? ''
                                        : date}
                                </Text>


                                <Text
                                    style={
                                        pdfStyles.cell
                                    }
                                >
                                    {isDuplicate
                                        ? ''
                                        : res.mobno || ''}
                                </Text>


                                <Text
                                    style={
                                        pdfStyles.cell
                                    }
                                >
                                    {res.custname ||
                                        ''}
                                </Text>


                                <Text
                                    style={
                                        pdfStyles.cell
                                    }
                                >
                                    {res.custadd ||
                                        ''}
                                </Text>


                                <Text
                                    style={
                                        pdfStyles.cell
                                    }
                                >
                                    {res.custstate ||
                                        ''}
                                </Text>


                                <Text
                                    style={
                                        pdfStyles.cell
                                    }
                                >
                                    {res.style || ''}
                                </Text>


                                <Text
                                    style={
                                        pdfStyles.cell
                                    }
                                >
                                    {res.size || ''}
                                </Text>


                                <Text
                                    style={
                                        pdfStyles.cell
                                    }
                                >
                                    {formatNumber(
                                        Number(
                                            res.salestock ||
                                                0
                                        ) * -1
                                    )}
                                </Text>

                            </View>

                        );

                    })}


                    {/* TOTAL */}

                    <View
                        style={
                            pdfStyles.totalRow
                        }
                    >

                        <Text
                            style={
                                pdfStyles.totalCell
                            }
                        ></Text>

                        <Text
                            style={
                                pdfStyles.totalCell
                            }
                        ></Text>

                        <Text
                            style={
                                pdfStyles.totalCell
                            }
                        ></Text>

                        <Text
                            style={
                                pdfStyles.totalCell
                            }
                        ></Text>

                        <Text
                            style={
                                pdfStyles.totalCell
                            }
                        ></Text>

                        <Text
                            style={
                                pdfStyles.totalCell
                            }
                        ></Text>

                        <Text
                            style={
                                pdfStyles.totalCell
                            }
                        ></Text>

                        <Text
                            style={
                                pdfStyles.totalCell
                            }
                        >
                            Total :
                        </Text>

                        <Text
                            style={
                                pdfStyles.totalCell
                            }
                        >
                            {formatNumber(totalPcs)}
                        </Text>

                    </View>

                </View>

            </Page>

        </Document>

    );
};


// =============================================================
// PDF STYLES
// =============================================================

const pdfStyles = StyleSheet.create({

    container: {
        padding: 20
    },

    title: {
        fontSize: 16,
        fontWeight: 'bold',
        marginBottom: 6,
        textAlign: 'center'
    },

    subTitle: {
        fontSize: 9,
        marginBottom: 15,
        textAlign: 'center'
    },

    table: {
        display: 'table',
        width: '100%',
        borderWidth: 1,
        borderColor: '#444'
    },

    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderColor: '#999'
    },

    headerCell: {
        fontSize: 7,
        fontWeight: 'bold',
        padding: 4,
        flex: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#999'
    },

    cell: {
        fontSize: 7,
        padding: 4,
        flex: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#ddd'
    },

    totalRow: {
        flexDirection: 'row',
        backgroundColor: '#fff1f2',
        borderTopWidth: 1,
        borderColor: '#555'
    },

    totalCell: {
        fontSize: 7,
        fontWeight: 'bold',
        padding: 4,
        flex: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#999'
    }

});

export default Rpt_SaleReport_Return;

